import { create } from 'multiformats/block';
import { sha256 as hasher } from 'multiformats/hashes/sha2';
import * as codec from '@ipld/dag-cbor';
// @ts-ignore
import charwise from 'charwise';
// @ts-ignore
import * as DbIndex from 'prolly-trees/db-index';
// @ts-ignore
import { bf, simpleCompare } from 'prolly-trees/utils';
// @ts-ignore
import { nocache as cache } from 'prolly-trees/cache';
export class IndexTree {
    cid = null;
    root = null;
}
const refCompare = (aRef, bRef) => {
    if (Number.isNaN(aRef))
        return -1;
    if (Number.isNaN(bRef))
        throw new Error('ref may not be Infinity or NaN');
    if (aRef === Infinity)
        return 1;
    // if (!Number.isFinite(bRef)) throw new Error('ref may not be Infinity or NaN')
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    return simpleCompare(aRef, bRef);
};
const compare = (a, b) => {
    const [aKey, aRef] = a;
    const [bKey, bRef] = b;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    const comp = simpleCompare(aKey, bKey);
    if (comp !== 0)
        return comp;
    return refCompare(aRef, bRef);
};
// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
export const byKeyOpts = { cache, chunker: bf(30), codec, hasher, compare };
// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
export const byIdOpts = { cache, chunker: bf(30), codec, hasher, compare: simpleCompare };
export function indexEntriesForChanges(changes, mapFn) {
    const indexEntries = [];
    changes.forEach(({ key: _id, value, del }) => {
        if (del || !value)
            return;
        let mapCalled = false;
        const mapReturn = mapFn({ _id, ...value }, (k, v) => {
            mapCalled = true;
            if (typeof k === 'undefined')
                return;
            indexEntries.push({
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
                key: [charwise.encode(k), _id],
                value: v || null
            });
        });
        if (!mapCalled && mapReturn) {
            indexEntries.push({
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
                key: [charwise.encode(mapReturn), _id],
                value: null
            });
        }
    });
    return indexEntries;
}
function makeProllyGetBlock(blocks) {
    return async (address) => {
        const block = await blocks.get(address);
        if (!block)
            throw new Error(`Missing block ${address.toString()}`);
        const { cid, bytes } = block;
        return create({ cid, bytes, hasher, codec });
    };
}
export async function bulkIndex(tblocks, inIndex, indexEntries, opts) {
    if (!indexEntries.length)
        return inIndex;
    if (!inIndex.root) {
        if (!inIndex.cid) {
            let returnRootBlock = null;
            let returnNode = null;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
            for await (const node of await DbIndex.create({ get: makeProllyGetBlock(tblocks), list: indexEntries, ...opts })) {
                const block = await node.block;
                await tblocks.put(block.cid, block.bytes);
                returnRootBlock = block;
                returnNode = node;
            }
            if (!returnNode || !returnRootBlock)
                throw new Error('failed to create index');
            return { root: returnNode, cid: returnRootBlock.cid };
        }
        else {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
            inIndex.root = await DbIndex.load({ cid: inIndex.cid, get: makeProllyGetBlock(tblocks), ...opts });
        }
    }
    const { root, blocks: newBlocks } = await inIndex.root.bulk(indexEntries);
    if (root) {
        for await (const block of newBlocks) {
            await tblocks.put(block.cid, block.bytes);
        }
        return { root, cid: (await root.block).cid };
    }
    else {
        return { root: null, cid: null };
    }
}
export async function loadIndex(tblocks, cid, opts) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    return await DbIndex.load({ cid, get: makeProllyGetBlock(tblocks), ...opts });
}
export async function applyQuery(crdt, resp, query) {
    if (query.descending) {
        resp.result = resp.result.reverse();
    }
    if (query.limit) {
        resp.result = resp.result.slice(0, query.limit);
    }
    if (query.includeDocs) {
        resp.result = await Promise.all(resp.result.map(async (row) => {
            const val = await crdt.get(row.id);
            const doc = val ? { _id: row.id, ...val.doc } : null;
            return { ...row, doc };
        }));
    }
    return {
        rows: resp.result.map(row => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
            row.key = charwise.decode(row.key);
            return row;
        })
    };
}
export function encodeRange(range) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    return range.map(key => charwise.encode(key));
}
export function encodeKey(key) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    return charwise.encode(key);
}
